import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { faClock, faEnvelope, faListOl } from "@fortawesome/pro-regular-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { Icon } from "@/components";
import { Modal } from "@/components/Modals/Modal";
import { ROUTE_CONFIG } from "@/routes/config";

import { CampaignStep, buildCampaignSteps } from "../utils/campaignSteps";

export interface CampaignStepsModalProps {
	isOpened: boolean;
	onClose: () => void;
	listId: number;
	listName: string;
	campaignName: string;
	/** `list.campaignConfig[uuid].initialNodes` — the saved sequence. */
	initialNodes: unknown;
}

const StepRow: FunctionComponent<{ step: CampaignStep }> = ({ step }) => {
	const { t } = useTranslation();

	return (
		<>
			{step.waitDays === null ? null : (
				<div className="flex flex-row items-center gap-3 pl-1 py-1.5 text-xs text-text-meta">
					<span className="flex items-center justify-center w-6 h-6 rounded-full border border-border-light">
						<Icon className="w-3 h-3" icon={faClock} />
					</span>
					{t("analytics.campaignModal.wait", { count: step.waitDays })}
				</div>
			)}
			<div className="flex flex-row items-start gap-3 rounded-xl border border-border-light px-3 py-2.5">
				<span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-action-200 text-[11px] font-bold text-brand-light shrink-0">
					{step.position}
				</span>
				<div className="flex flex-row items-center gap-2 text-sm text-text">
					<Icon
						className="w-3.5 h-3.5 text-brand-light"
						icon={step.channel === "email" ? faEnvelope : faLinkedinIn}
					/>
					{t(`analytics.campaignModal.steps.${step.kind}`)}
				</div>
			</div>
		</>
	);
};

/**
 * A read-only summary of what a campaign sends, in order.
 *
 * Deliberately not a second campaign builder — it answers "what does this campaign do"
 * without leaving the report, and links out to the builder for anything more.
 */
export const CampaignStepsModal: FunctionComponent<CampaignStepsModalProps> = ({
	isOpened,
	onClose,
	listId,
	listName,
	campaignName,
	initialNodes,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const steps = buildCampaignSteps(initialNodes);

	return (
		<Modal
			handleClose={onClose}
			handleSave={() => navigate(`${ROUTE_CONFIG.LISTS}/create/copywriting/${listId}`)}
			icon={<Icon className="w-4 h-4" icon={faListOl} />}
			isOpened={isOpened}
			submitButtonText={t("analytics.campaignModal.openBuilder")}
			subtitle={t("analytics.campaignModal.subtitle", {
				count: steps.length,
				list: listName,
			})}
			title={campaignName}
		>
			<div className="flex flex-col gap-1.5 w-full">
				{steps.length === 0 ? (
					<p className="text-sm text-text-meta">{t("analytics.campaignModal.noSteps")}</p>
				) : (
					steps.map((step) => <StepRow key={`${step.position}-${step.kind}`} step={step} />)
				)}
			</div>
		</Modal>
	);
};
